'use client';

import { forwardRef, useCallback } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableState } from '../../hooks/use-controllable-state';
import { ToastContext } from './context/toast.context';
import { toastRootRecipe } from './styles/toast-root.styles';
import { ToastIcon } from './toast-icon';
import type { ToastRootPropsInterface } from './types/toast-root.types';

export const ToastRoot = forwardRef<HTMLDivElement, ToastRootPropsInterface>(
  (props: ToastRootPropsInterface, forwardedRef) => {
    const {
      tone = 'info',
      children,
      className,
      css: cssProp,
      isOpen,
      onClose,
      role = 'status',
      ...rest
    } = props;

    const styles = css(toastRootRecipe.raw({ tone }), cssProp);

    const [openState, setOpenState] = useControllableState({
      prop: isOpen,
      defaultProp: true,
    });

    const handleClose = useCallback(() => {
      setOpenState(false);
      onClose?.();
    }, [onClose, setOpenState]);

    if (!openState) {
      return null;
    }

    return (
      <ToastContext.Provider value={{ tone, handleClose }}>
        <div
          className={cx('c11n', styles, className)}
          data-c11n-component="Toast.Root"
          data-c11n-version={__C11N_VERSION__}
          ref={forwardedRef}
          role={role}
          {...rest}
        >
          <ToastIcon />
          {children}
        </div>
      </ToastContext.Provider>
    );
  },
);

ToastRoot.displayName = 'Toast.Root';
